import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { push } from 'react-router-redux'
import { requestProUpgrade, resetProUpgrade } from '../actions/license';

/**
 * ApiIntegrationView (/api)
 *
 * Non-technical page explaining how customers can export their sales
 * automatically to their ERP / SAP using the VACWAY API.
 *
 * • LICENCIA_BASICA  → explains the feature, shows "Request PRO" upgrade block.
 * • LICENCIA_PRO     → shows the Bearer Token (Customer._id = auth.id)
 *                      and a button to the technical Integraciones docs page.
 */

const ACCENT = '#f59e0b';
const DARK   = '#001d3a';

class ApiIntegrationView extends Component {
  constructor(props) {
    super(props);
    this.state = { copied: false, requestSent: false };
    this.handleCopy        = this.handleCopy.bind(this);
    this.handleRequest     = this.handleRequest.bind(this);
  }

  componentDidMount() {
    this.props.resetProUpgrade();
  }

  handleCopy() {
    const { customerId } = this.props;
    if (!customerId) return;
    navigator.clipboard.writeText(customerId).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2500);
    });
  }

  handleRequest() {
    this.props.requestProUpgrade();
  }

  render() {
    const { type, customerId, push, proUpgradeLoading, proUpgradeSent, proUpgradeError } = this.props;
    const isPro   = type === 'LICENCIA_PRO';
    const { copied } = this.state;

    return (
      <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", background: '#f4f6f9', minHeight: '100vh', paddingBottom: 60 }}>

        {/* ── Hero ── */}
        <div style={{ background: `linear-gradient(135deg, ${DARK} 0%, #003366 100%)`, padding: '40px 48px 36px', borderBottom: `4px solid ${ACCENT}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <span style={{ background: ACCENT, color: DARK, fontWeight: 700, fontSize: 11, padding: '3px 10px', borderRadius: 20, letterSpacing: 1, textTransform: 'uppercase' }}>
              Automatización
            </span>
            {isPro && (
              <span style={{ background: '#10b981', color: '#fff', fontWeight: 700, fontSize: 11, padding: '3px 10px', borderRadius: 20, letterSpacing: 1, textTransform: 'uppercase' }}>
                PRO activo
              </span>
            )}
          </div>
          <h1 style={{ fontSize: 30, fontWeight: 700, color: '#fff', margin: '0 0 10px' }}>
            Conecta VACWAY con tu ERP o sistema interno
          </h1>
          <p style={{ color: '#a8c8e8', fontSize: 15, margin: 0, maxWidth: 640, lineHeight: 1.7 }}>
            Exporta tus ventas automáticamente a tu sistema de gestión —
            sin exportaciones manuales, sin copiar y pegar. Directo desde VACWAY a tu software.
          </p>
        </div>

        <div style={{ padding: '36px 0' }}>

          {/* ── Row 1: How it works + Use cases ── */}
          <div className="api-grid" style={{ marginBottom: 0 }}>
            <Card title="¿Cómo funciona?">
              <p style={S.para}>
                VACWAY dispone de una <strong>API de ventas</strong> que permite a tu equipo de IT
                o a tu proveedor tecnológico conectar tu ERP, SAP, Navision, Odoo o cualquier
                otro sistema para recibir tus ventas de forma automática.
              </p>
              <p style={S.para}>
                No necesitas hacer nada manualmente. Una vez configurado, el flujo es:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
                <Step n="1" title="Tu sistema se identifica" desc="Envía tu Token de acceso en la cabecera de la petición. Es único y personal." />
                <Step n="2" title="Solicita las ventas" desc="Llama al endpoint con el rango de fechas que necesitas (hoy, esta semana, este mes…)." />
                <Step n="3" title="Recibe los datos" desc="La API devuelve el listado de ventas en formato estándar, listo para importar." />
              </div>
            </Card>

            <Card title="¿Para qué lo puedes usar?">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {USE_CASES.map(uc => (
                  <div key={uc} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e8edf5' }}>
                    <span style={{ color: ACCENT, fontSize: 18, lineHeight: 1, flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: 14, color: '#374151', lineHeight: 1.5 }}>{uc}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* ── Row 2: PRO / BASIC conditional block (full width) ── */}
          {isPro ? (
            <ProSection customerId={customerId} copied={copied} onCopy={this.handleCopy} onGoToDocs={() => push('/integraciones')} />
          ) : (
            <BasicSection
              loading={proUpgradeLoading}
              sent={proUpgradeSent}
              error={proUpgradeError}
              onRequest={this.handleRequest}
            />
          )}
        </div>
      </div>
    );
  }
}

/* ─────────────────────────────── Sub-components ─────────────────────────────── */

const Card = ({ title, children }) => (
  <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', marginBottom: 24, overflow: 'hidden' }}>
    <div style={{ padding: '14px 24px', background: DARK, color: '#fff', fontSize: 15, fontWeight: 600 }}>{title}</div>
    <div style={{ padding: '22px 24px' }}>{children}</div>
  </div>
);

const Step = ({ n, title, desc }) => (
  <div style={{ background: '#f8fafc', border: '1px solid #e8edf5', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
    <div style={{ width: 32, height: 32, borderRadius: '50%', background: ACCENT, color: DARK, fontWeight: 800, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{n}</div>
    <div>
      <div style={{ fontWeight: 700, fontSize: 13, color: DARK, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>{desc}</div>
    </div>
  </div>
);

const ProSection = ({ customerId, copied, onCopy, onGoToDocs }) => (
  <div style={{ background: '#fff', borderRadius: 10, border: `2px solid ${ACCENT}`, boxShadow: '0 4px 20px rgba(245,158,11,0.15)', marginBottom: 24, overflow: 'hidden' }}>
    <div style={{ padding: '14px 24px', background: `linear-gradient(135deg, ${DARK} 0%, #003366 100%)`, color: '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 18 }}>🔑</span>
      <span style={{ fontSize: 15, fontWeight: 700 }}>Tu Token de acceso a la API</span>
      <span style={{ marginLeft: 'auto', background: '#10b981', color: '#fff', fontWeight: 700, fontSize: 11, padding: '2px 10px', borderRadius: 20, letterSpacing: 1, textTransform: 'uppercase' }}>Activo</span>
    </div>
    <div style={{ padding: '24px' }}>
      <p style={S.para}>
        Este es tu <strong>Token Bearer</strong> personal. Tu equipo de IT lo usará para autenticar
        las peticiones a la API. Trátalo como una contraseña: no lo compartas públicamente.
      </p>
      <div style={{ background: '#f0f4f8', border: '1px solid #d2dce8', borderRadius: 8, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <code style={{ fontFamily: 'monospace', fontSize: 14, color: DARK, wordBreak: 'break-all', flex: 1 }}>
          {customerId || '—'}
        </code>
        <button
          onClick={onCopy}
          style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: copied ? '#10b981' : ACCENT, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.2s' }}
        >
          {copied ? '¡Copiado!' : 'Copiar'}
        </button>
      </div>
      <p style={{ ...S.para, margin: '0 0 20px' }}>
        ¿Cómo se usa? Tu equipo de IT debe enviarlo en la cabecera HTTP de cada petición:
      </p>
      <pre style={{ background: '#0d1117', color: '#c9d1d9', fontFamily: 'monospace', fontSize: 13, padding: '14px 18px', borderRadius: 8, overflowX: 'auto', marginBottom: 20 }}>
        {`Authorization: Bearer ${customerId || '<TU_TOKEN>'}`}
      </pre>
      <button
        onClick={onGoToDocs}
        style={{ padding: '12px 28px', borderRadius: 40, border: 'none', background: DARK, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
      >
        📄 Ver documentación técnica (Integraciones)
      </button>
    </div>
  </div>
);

const BasicSection = ({ loading, sent, error, onRequest }) => (
  <div style={{ background: '#fff', borderRadius: 10, border: `2px solid #e5e7eb`, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', marginBottom: 24, overflow: 'hidden' }}>
    <div style={{ padding: '14px 24px', background: 'linear-gradient(135deg, #374151 0%, #4b5563 100%)', color: '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 18 }}>🔒</span>
      <span style={{ fontSize: 15, fontWeight: 700 }}>Función disponible con licencia PRO</span>
    </div>
    <div style={{ padding: '28px 24px' }}>
      {sent ? (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 17, fontWeight: 600, color: DARK, marginBottom: 8 }}>¡Solicitud enviada!</div>
          <p style={{ ...S.para, maxWidth: 440, margin: '0 auto' }}>
            Nuestro equipo se pondrá en contacto contigo en breve para gestionar la actualización a PRO
            y darte acceso a la API de exportación automática.
          </p>
        </div>
      ) : (
        <>
          <p style={S.para}>
            El acceso a la API de exportación es una funcionalidad exclusiva de la licencia
            <strong> PRO</strong>. Con ella podrás conectar VACWAY directamente con tu ERP, SAP
            o cualquier sistema interno, sin intervención manual.
          </p>
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '14px 18px', marginBottom: 20 }}>
            <strong style={{ color: '#92400e', fontSize: 14 }}>¿Qué obtienes al actualizar a PRO?</strong>
            <ul style={{ margin: '10px 0 0', paddingLeft: 20, color: '#78350f', fontSize: 13, lineHeight: 1.8 }}>
              <li>Token de acceso personal para autenticar peticiones</li>
              <li>Exportación automática de ventas en cualquier intervalo</li>
              <li>Acceso a la documentación técnica completa de la API</li>
              <li>Todas las funcionalidades incluidas en la licencia PRO</li>
            </ul>
          </div>
          {error && (
            <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>
              No se pudo enviar la solicitud. Inténtalo de nuevo más tarde.
            </p>
          )}
          <button
            onClick={onRequest}
            disabled={loading}
            style={{ padding: '13px 32px', borderRadius: 40, border: 'none', background: loading ? '#d97706' : ACCENT, color: '#fff', fontWeight: 700, fontSize: 15, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.8 : 1, display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            ⭐ {loading ? 'Enviando solicitud…' : 'Solicitar actualización a PRO'}
          </button>
        </>
      )}
    </div>
  </div>
);

/* ─────────────────────────────── Data ─────────────────────────────── */

const USE_CASES = [
  'Volcar ventas diarias en tu ERP automáticamente',
  'Sincronizar ingresos con tu sistema de contabilidad (SAP, Navision…)',
  'Generar informes de ingresos sin exportar nada manualmente',
  'Integrar con cuadros de mando BI (Power BI, Tableau…)',
  'Automatizar la conciliación de cobros con tu banco',
  'Alimentar tu sistema de facturación',
];

const S = {
  para: { color: '#4a5568', fontSize: 14, lineHeight: 1.7, margin: '0 0 12px' },
};

/* ─────────────────────────────── Redux ─────────────────────────────── */

const mapStateToProps = (state) => ({
  customerId: state.auth.id,
  type:       state.auth.type,
  role:       state.auth.role,
  proUpgradeLoading: state.license.loading,
  proUpgradeSent:    state.license.sent,
  proUpgradeError:   state.license.error,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  requestProUpgrade,
  resetProUpgrade,
  push,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ApiIntegrationView);
